import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import TypeFilterCard from "../../components/filters/TypeFilterCard";
import { Button, Card, useTheme } from "@mui/material";
import DashboardTable from "../../components/tables/DashboardShipmentsTable";
import icon1 from "../../../../../assets/dashboardicon.png";
import icon2 from "../../../../../assets/dashboardiconn.png";
import AppSearchDropDown2 from "../../../../../views/components/common/filter/AppSearchDropdown2";
import AppBarChart from "../../components/charts/AppBarChart";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import {
  parseQueryParamsForParterAnalyticsFilters,
  PartnerAnalyticsFilters,
  PartnerAnalyticsSelectedFilters,
} from "../../../../../models/Dashboard/PartnerDashboard/PartnerAnalyticsFilters";
import { PartnerAnalyticsChartData } from "../../../../../models/Dashboard/PartnerDashboard/PartnerAnalytics";
import {
  ShipmentStatus,
  ShipmentType,
} from "../../../../../models/Shipments/ShipmentStatus";
import AppStackChart from "../../components/charts/AppStackChart";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { useNotification } from "../../../../../utils/NotificationsContext";
import LoaderOverlay from "../../../../../views/components/common/pages/AppLoader";

type Filter = {
  type: string;
  value: string;
};

const PartnerAnalyticsDashboard: React.FC = () => {
  const theme = useTheme();
  const { partnerAnalyticsDashboardStore, customerStore } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiltersData, setSelectedFiltersData] = useState<Filter[]>([]);
  const [loading, setLoading] = useState(false);
  const { showError } = useNotification();

  const PARTNERT_ANALYTICS_FILTERS_KEY = "PARTNERT_ANALYTICS_FILTERS_KEY";
  const colors = ["#4285F4", "#34A853", "#FBBC05", "#DB4437"];
  let apiTimer: any;

  const shipmentStatusOptions = ShipmentStatus.map((status) => ({
    id: status.id.toString(),
    value: status.value,
  }));

  const shipmentTypeOptions = ShipmentType.map((status) => ({
    id: status.id.toString(),
    value: status.value,
  }));

  const unsyncedShipments =
    (partnerAnalyticsDashboardStore.response as PartnerAnalyticsChartData)
      ?.unsynced_shipments || [];

  const initialFilters: PartnerAnalyticsFilters =
    parseQueryParamsForParterAnalyticsFilters(
      localStorage.getItem(PARTNERT_ANALYTICS_FILTERS_KEY)
        ? JSON.parse(localStorage.getItem(PARTNERT_ANALYTICS_FILTERS_KEY)!)
        : null,
      searchParams
    );
  const [filters, setFilters] =
    useState<PartnerAnalyticsFilters>(initialFilters);

  const [selectedFilters, setSelectedFilters] =
    useState<PartnerAnalyticsSelectedFilters>({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
    });

  useEffect(() => {
    updateSelectedFilters();
  }, [filters]);

  useEffect(() => {
    handleApply();
  }, []);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });
    setSearchParams(newSearchParams);
  }, [selectedFilters]);

  const updateSelectedFilters = () => {
    localStorage.setItem(
      PARTNERT_ANALYTICS_FILTERS_KEY,
      JSON.stringify(filters)
    );

    const start_date = filters.start_date.format("YYYY-MM-DD");
    const end_date = filters.end_date.format("YYYY-MM-DD");

    setSelectedFilters((_) => ({
      ...filters,
      start_date: start_date,
      end_date: end_date,
    }));

    const newFilters = [];
    if (filters.partner_id && filters.partner_id.length > 0) {
      newFilters.push({ type: "Partner", value: filters.partner_id[0].value });
    }
    if (filters.shipment_type) {
      filters.shipment_type.forEach((type) =>
        newFilters.push({ type: "Order Label", value: type.value })
      );
    }
    if (filters.shipment_status) {
      filters.shipment_status.forEach((status) =>
        newFilters.push({ type: "Status", value: status.value })
      );
    }
    setSelectedFiltersData(newFilters);
  };

  const handleApply = async () => {
    setLoading(true);

    try {
      await partnerAnalyticsDashboardStore.getShipmentsChartData({
        partner_id: selectedFilters.partner_id,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
      });

      await partnerAnalyticsDashboardStore.getShipments({
        page: selectedFilters.page,
        limit: 30,
        sortBy: selectedFilters.sortBy,
        sortType: selectedFilters.sortType,
        search: selectedFilters.search ?? undefined,
        shipment_type: selectedFilters.shipment_type
          ?.map((item) => item.id)
          .map(Number),
        status: selectedFilters.shipment_status
          ?.map((item) => item.id)
          .map(Number),
        c_id: selectedFilters.partner_id,
      });
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details.");
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    if (range.startDate && range.endDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: range.startDate!,
        end_date: range.endDate!,
      }));
    }
  };

  const handleRemoveFilter = (index: number) => {
    const filterToRemove = selectedFiltersData[index];
    const updatedFilters = selectedFiltersData.filter((_, i) => i !== index);
    setSelectedFiltersData(updatedFilters);

    switch (filterToRemove.type) {
      case "Partner":
        setFilters((prev) => ({
          ...prev,
          partner_id: [],
        }));
        break;
      case "Order Label":
        setFilters((prev) => ({
          ...prev,
          shipment_type:
            prev.shipment_type?.filter(
              (item) => item.value !== filterToRemove.value
            ) || null,
        }));
        break;
      case "Status":
        setFilters((prev) => ({
          ...prev,
          shipment_status:
            prev.shipment_status?.filter(
              (item) => item.value !== filterToRemove.value
            ) || null,
        }));
        break;
      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setFilters((_) => ({
      ...initialFilters,
      partner_id: [],
      shipment_type: null,
      shipment_status: null,
    }));

    setSelectedFiltersData([]);
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setFilters((prev) => ({
      ...prev,
      limit: newLimit,
      page: 1,
    }));
  };

  return (
    <>
      <LoaderOverlay loading={loading} />
      <Box sx={{ margin: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.customColors.white,
            padding: 4,
            borderRadius: 2,
            marginBottom: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "20%" }}>
              <CustomTypography
                variant="heading7"
                color={theme.palette.customColors.tableGrayHeadingColor}
                style={{ marginBottom: 10, fontSize: "0.8rem" }}
              >
                {"Partner"}
              </CustomTypography>
              <AppSearchDropDown2
                isMultiSelect={false}
                title="Select Partner"
                value={filters.partner_id}
                fontSize="0.7rem"
                list={customerStore.customers.map((item) => {
                  return { id: String(item.id), value: item.name! };
                })}
                local={false}
                handleSearch={(searchText?: string) => {
                  if (apiTimer) clearTimeout(apiTimer);
                  if (searchText && searchText.length > 2)
                    apiTimer = setTimeout(() => {
                      customerStore
                        .searchCustomers({
                          page_no: 1,
                          search_text: searchText,
                          is_partner: true,
                        })
                        .catch((err) => {});
                    }, 1500);
                }}
                handleSelect={(items) => {
                  setFilters((prev) => ({
                    ...prev,
                    partner_id: items,
                  }));
                }}
              />
            </Box>

            <Box sx={{ width: "20%" }}>
              <CustomTypography
                variant="heading7"
                color={theme.palette.customColors.tableGrayHeadingColor}
                style={{ marginBottom: 10, fontSize: "0.8rem" }}
              >
                {"Shipment Type"}
              </CustomTypography>
              <AppDropDown
                title=""
                fontSize="0.7rem"
                isMultiSelect={true}
                valuesList={filters.shipment_type?.map((item) => item.id) || []}
                list={shipmentTypeOptions || []}
                onMultiSelectionChange={(ids) => {
                  setFilters((prev) => ({
                    ...prev,
                    shipment_type: shipmentTypeOptions.filter((item) =>
                      ids.includes(item.id)
                    ),
                  }));
                }}
              />
            </Box>

            <Box sx={{ width: "20%" }}>
              <CustomTypography
                variant="heading7"
                color={theme.palette.customColors.tableGrayHeadingColor}
                style={{ marginBottom: 10, fontSize: "0.8rem" }}
              >
                Status
              </CustomTypography>
              <AppDropDown
                title=""
                fontSize="0.7rem"
                isMultiSelect={true}
                valuesList={
                  filters.shipment_status?.map((item) => item.id) || []
                }
                list={shipmentStatusOptions}
                onMultiSelectionChange={(ids) => {
                  setFilters((prev) => ({
                    ...prev,
                    shipment_status: shipmentStatusOptions.filter((item) =>
                      ids.includes(item.id)
                    ),
                  }));
                }}
              />
            </Box>

            <Box sx={{ width: "20%" }}>
              <CustomTypography
                variant="heading7"
                color={theme.palette.customColors.tableGrayHeadingColor}
                style={{ marginBottom: 10, fontSize: "0.8rem" }}
              >
                Date Range
              </CustomTypography>
              <DateRangePickerNew
                onRangeSelect={handleRangeSelect}
                startDate={filters.start_date}
                endDate={filters.end_date}
              />
            </Box>

            <Box sx={{ marginTop: 3.1 }}>
              <AppButton
                variant="primary"
                label="Apply"
                onClick={handleApply}
                disabled={loading}
              />
            </Box>

            <Box sx={{ marginTop: 3.1 }}>
              <AppButton
                variant="secondary"
                label="Clear"
                customStyles={{ width: "100%" }}
                onClick={() => handleClearAllFilters()}
              />
            </Box>
          </Box>

          <Box sx={{ paddingTop: "15px" }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                borderRadius: 2,
                paddingLeft: "40px",
                paddingRight: "40px",
              }}
            >
              {selectedFiltersData.map((filter, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.background.paper
                        : theme.palette.customColors.lightGreyColor,
                    borderRadius: 5,
                    border: "1px solid #E2E2E2",
                    height: "30px",
                    paddingRight: "5px",
                  }}
                >
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.blackBorder}
                    style={{ paddingLeft: "10px", paddingRight: "5px" }}
                  >
                    {filter.value}
                  </CustomTypography>
                  <Button
                    onClick={() => handleRemoveFilter(index)}
                    sx={{
                      width: "14px",
                      height: "14px",
                      minWidth: "14px",
                      minHeight: "14px",
                      fontSize: "15px",
                      padding: "0px",
                      border: "1px solid #E2E2E2",
                      borderRadius: "50%",
                      backgroundColor: "#FFFFFF",
                      cursor: "pointer",
                      overflow: "hidden",
                      "&:hover": {
                        backgroundColor: "#F0F0F0",
                      },
                    }}
                  >
                    ×
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ marginBottom: 4, gap: 4, display: "flex", height: "500px" }}>
          <AppStackChart
            title="Inventory"
            data={
              partnerAnalyticsDashboardStore.response?.pack_wise_quantity ?? []
            }
            data_keys_config={[
              {
                key: "available_quantity",
                color: theme.palette.blue?.main,
                activeColor: theme.palette.blue?.light,
              },
              {
                key: "used_quantity",
                color: theme.palette.skyBlue?.main,
                activeColor: theme.palette.skyBlue?.light,
              },
            ]}
            layout="horizontal"
            sx={{ width: "70%", height: "100%", p: 4 }}
          />

          <Card
            sx={{
              backgroundColor: theme.palette.customColors.white,
              padding: "32px",
              borderRadius: "4px",
              width: "28%",
            }}
          >
            <Box>
              <CustomTypography
                variant="heading4"
                color={theme.palette.customColors.blackBorder}
              >
                Sync Status
              </CustomTypography>
              <CustomTypography
                variant="heading7"
                fontWeight={700}
                color={theme.palette.customColors.tableGrayHeadingColor}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "28px",
                  padding: "0px 14px 0px 3px",
                }}
              >
                PARTNER<span>TOTAL</span>
              </CustomTypography>
            </Box>
            <hr style={{ border: "1px solid ##E2E2E2" }} />
            {unsyncedShipments.length === 0 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  width: "100%",
                  height: "70%",
                }}
              >
                <CustomTypography
                  variant="heading7"
                  style={{
                    lineHeight: 1.3,
                  }}
                >
                  Great News! All shipments are successfully synced and on their
                  way!
                </CustomTypography>
              </Box>
            )}
            {unsyncedShipments.length > 0 &&
              unsyncedShipments.map(
                (
                  shipment: { partner_name: string; requests_count: number },
                  index: number
                ) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: "14px 15px 7px 3px",
                    }}
                  >
                    <CustomTypography
                      variant="heading4"
                      color={colors[index % colors.length]}
                      sx={{ fontWeight: "bold" }}
                    >
                      {shipment.partner_name}
                    </CustomTypography>
                    <CustomTypography
                      variant="heading6"
                      fontWeight={700}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                      }}
                    >
                      {shipment.requests_count}
                    </CustomTypography>
                  </Box>
                )
              )}
          </Card>
        </Box>
        <Box sx={{ marginBottom: 4, gap: 4, display: "flex", height: "500px" }}>
          <AppBarChart
            title="Weekly Shipments"
            data={
              partnerAnalyticsDashboardStore.response
                ?.shipments_count_monthwise ?? []
            }
            dataKey="month_year"
            data_keys_config={[
              {
                key: "total_requests",
                color: theme.palette.blue?.main,
                activeColor: theme.palette.blue?.light,
              },
            ]}
            layout="vertical"
            sx={{ width: "70%", height: "100%", p: 4 }}
          />

          <Card
            sx={{
              backgroundColor: theme.palette.customColors.white,
              borderRadius: "4px",
              width: "28%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                mt: 4,
                ml: 4,
                gap: 2,
              }}
            >
              <CustomTypography
                variant="heading4"
                color={theme.palette.customColors.blackBorder}
              >
                Shipment TAT
              </CustomTypography>
              <CustomTypography
                variant="body3"
                color={theme.palette.customColors.blackBorder}
                style={{ marginTop: 4 }}
              >
                Number of shipments is the count of shipments created for the
                selected filters. TAT data is collected from 10th March, 2025.
              </CustomTypography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: 8,
              }}
            >
              <Box
                sx={{
                  width: { sm: 180, md: 210 },
                  height: { sm: 180, md: 210 },
                  borderRadius: "50%",
                  backgroundColor: "#F3F4F6",
                  position: "absolute",
                }}
              />

              <Box
                sx={{
                  width: { sm: 120, md: 145 },
                  height: { sm: 120, md: 145 },
                  borderRadius: "50%",
                  backgroundColor: "#6455ED",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  zIndex: 1,
                  gap: 1,
                }}
              >
                <CustomTypography
                  variant="heading1"
                  color="white"
                  style={{ fontSize: "36px" }}
                >
                  {partnerAnalyticsDashboardStore.response
                    ?.total_shipments_count?.total_count ?? "00"}
                </CustomTypography>

                <CustomTypography
                  variant="heading7"
                  color="white"
                  sx={{ textAlign: "center", display: "block" }}
                >
                  Total No. of Shipments
                </CustomTypography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                mt: 12,
                gap: 5,
                paddingLeft: "10px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#6455ED",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                  }}
                >
                  <Box
                    component="img"
                    src={icon1}
                    alt="Dispatch Icon"
                    sx={{
                      width: 30,
                      height: 30,
                    }}
                  />
                </Box>
                <Box sx={{ paddingLeft: 1 }}>
                  <CustomTypography variant="heading2">
                    {`${
                      partnerAnalyticsDashboardStore.response?.shipping_tat
                        ?.avg_days ?? "00"
                    }.${
                      partnerAnalyticsDashboardStore.response?.shipping_tat
                        ?.avg_hours ?? "00"
                    }`}
                  </CustomTypography>

                  <CustomTypography
                    variant="heading8"
                    color="#6b7280"
                    fontWeight={700}
                  >
                    Dispatch TAT
                  </CustomTypography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#EE9C03",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                  }}
                >
                  <Box
                    component="img"
                    src={icon2}
                    alt="Delivery Icon"
                    sx={{
                      width: 30,
                      height: 30,
                    }}
                  />
                </Box>
                <Box sx={{ paddingLeft: 1 }}>
                  <CustomTypography variant="heading2">
                    {`${
                      partnerAnalyticsDashboardStore.response?.delivery_tat
                        ?.avg_days ?? "00"
                    }.${
                      partnerAnalyticsDashboardStore.response?.delivery_tat
                        ?.avg_hours ?? "00"
                    }`}
                  </CustomTypography>

                  <CustomTypography
                    variant="heading8"
                    color="#6b7280"
                    fontWeight={700}
                  >
                    Delivery TAT
                  </CustomTypography>
                </Box>
              </Box>
            </Box>
          </Card>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "space-aroundly",

            "& > *": {
              flex: " calc(20% - 16px)",
              maxWidth: "calc(20% - 16px)",
            },
          }}
        >
          {partnerAnalyticsDashboardStore.statusFilters.map((s, index) => (
            <Box key={index}>
              <TypeFilterCard
                title={s.label}
                value={s.count}
                icon={s.icon}
                color={""}
              />
            </Box>
          ))}
        </Box>
        {partnerAnalyticsDashboardStore.shipmentsReponse.list &&
          partnerAnalyticsDashboardStore.shipmentsReponse.list.length > 0 && (
            <DashboardTable
              shipments={partnerAnalyticsDashboardStore.shipmentsReponse.list}
              limit={filters.limit}
              maxHeight={600}
              onPageChange={(newPage: number) => {
                setFilters((prev) => ({ ...prev, page: newPage }));
              }}
              page_no={selectedFilters.page}
              total_pages={
                partnerAnalyticsDashboardStore.shipmentsReponse.total_pages
              }
              total_count={
                partnerAnalyticsDashboardStore.shipmentsReponse.total_count
              }
              onSortChange={(column, sortType) => {
                setFilters((prev) => ({
                  ...prev,
                  sortBy: column,
                  sortType: sortType,
                }));
              }}
              sortBy={selectedFilters.sortBy}
              sortType={selectedFilters.sortType}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          )}
        {!partnerAnalyticsDashboardStore.shipmentsReponse.list ||
          (partnerAnalyticsDashboardStore.shipmentsReponse.list.length ===
            0 && <p>No Data</p>)}
      </Box>
    </>
  );
};

export default observer(PartnerAnalyticsDashboard);
